/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Flex, Text, TextField } from '@servicetitan/anvil2';
import { PressureSetPointsRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const PressureSetPointsForm: FC<{
    className?: string;
    onSubmit?: (params: PressureSetPointsRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            liquid_line: new InputFieldState<string>('').validators(value => !value && 'Required'),
            discharge_line: new InputFieldState<string>('').validators(
                value => !value && 'Required'
            ),
            suction_line: new InputFieldState<string>('').validators(value => !value && 'Required'),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: PressureSetPointsRequestDto = {
            tool_name: 'pressure-set-points',
            tool_input: {
                liquid_line: Number(formState.$.liquid_line.$),
                discharge_line: Number(formState.$.discharge_line.$),
                suction_line: Number(formState.$.suction_line.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                label="Liquid Line Pressure (PSI)"
                type="number"
                value={formState.$.liquid_line.value}
                onChange={e => formState.$.liquid_line.onChange(e.target.value)}
            />
            <TextField
                required
                label="Discharge Line Pressure (PSI)"
                type="number"
                value={formState.$.discharge_line.value}
                onChange={e => formState.$.discharge_line.onChange(e.target.value)}
            />
            <TextField
                required
                label="Suction Line Pressure (PSI)"
                type="number"
                value={formState.$.suction_line.value}
                onChange={e => formState.$.suction_line.onChange(e.target.value)}
            />

            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
